import React from 'react';
import { useStore } from '../context/StoreContext';
import { Link } from 'react-router-dom';
import { downloadInvoice } from '../utils/invoice';

export default function OrderHistory() {
  const { orders, isLoggedIn, setLoginModalOpen, userEmail } = useStore();

  if (!isLoggedIn) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-fuchsia-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-history-line text-4xl text-indigo-600"></i>
          </div>
          <h1 className="text-3xl font-display font-bold mb-3">Order History</h1>
          <p className="text-slate-500 mb-6">Please login to view your orders</p>
          <button onClick={() => setLoginModalOpen(true)} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg">
            Login / Sign Up
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      'Confirmed': 'bg-emerald-100 text-emerald-700',
      'Shipped': 'bg-blue-100 text-blue-700',
      'Out for Delivery': 'bg-amber-100 text-amber-700',
      'Delivered': 'bg-green-100 text-green-700',
      'Cancelled': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="relative mb-10">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-fuchsia-600/10 rounded-3xl blur-3xl -z-10"></div>
        <h1 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-indigo-700 to-fuchsia-700 bg-clip-text text-transparent">Order History</h1>
        <p className="text-slate-500 mt-2">Track and manage all your purchases</p>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <i className="ri-shopping-bag-line text-5xl text-slate-300 mb-4"></i>
          <h3 className="text-xl font-semibold mb-2">No orders yet</h3>
          <p className="text-slate-500 mb-6">Start shopping to see your orders here</p>
          <Link to="/" className="bg-indigo-600 text-white px-6 py-2 rounded-full inline-block">Shop Now</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map(order => (
            <div key={order.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition group">
              {/* Order header */}
              <div className="p-5 bg-gradient-to-r from-slate-50 to-white border-b flex flex-wrap justify-between items-center gap-3">
                <div>
                  <p className="text-xs text-slate-500">ORDER PLACED</p>
                  <p className="font-semibold">{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">TOTAL AMOUNT</p>
                  <p className="font-bold text-lg">₹{order.total.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">ORDER ID</p>
                  <p className="font-mono text-sm">#{order.id.slice(-8)}</p>
                </div>
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>

              {/* Order items */}
              <div className="p-5 space-y-3">
                {order.items.slice(0, 2).map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-center">
                    <img src={item.image} className="w-12 h-12 object-cover rounded-lg" alt={item.name} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-slate-500">Qty: {item.quantity} | Size: {item.size}</p>
                    </div>
                    <p className="font-semibold text-sm">₹{(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
                {order.items.length > 2 && (
                  <p className="text-xs text-slate-500">+{order.items.length - 2} more items</p>
                )}
              </div>

              {/* Actions */}
              <div className="p-5 bg-slate-50 flex flex-wrap gap-3 justify-end border-t">
                <Link to={`/tracking/${order.id}`} className="text-indigo-600 text-sm font-semibold hover:underline flex items-center gap-1">
                  <i className="ri-map-pin-line"></i> Track Order
                </Link>
                <button 
                     onClick={() => downloadInvoice(order, userEmail)}
                       className="text-slate-600 text-sm font-semibold hover:underline flex items-center gap-1">
                     <i className="ri-file-copy-line"></i> Download Invoice
                 </button>
                <button className="text-rose-600 text-sm font-semibold hover:underline flex items-center gap-1">
                  <i className="ri-question-line"></i> Need Help?
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}